import React, { useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useSourcing } from '../../contexts/SourcingContext';
import { NavigationBar } from '../NavigationBar';
import { CalendarIcon, DollarSignIcon, ZapIcon } from 'lucide-react';
const SourcingEventLayout: React.FC = () => {
  const {
    sourcingEvent
  } = useSourcing();
  const [autoNegotiate, setAutoNegotiate] = useState(false);
  // Redirect to home if no active sourcing event
  if (!sourcingEvent) {
    return <Navigate to="/" replace data-id="element-694" />;
  }
  // Format date safely
  const formatDate = (dateValue: any) => {
    if (!dateValue) return 'Not specified';
    try {
      if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString();
      }
      if (typeof dateValue === 'string') {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
        return dateValue;
      }
      return String(dateValue);
    } catch (error) {
      return 'Error displaying date';
    }
  };
  // Format budget safely
  const formatBudget = (budgetValue: any) => {
    if (budgetValue === null || budgetValue === undefined) return 'Not specified';
    try {
      if (typeof budgetValue === 'number') {
        return `€${budgetValue.toLocaleString()}`;
      }
      if (typeof budgetValue === 'string') {
        const budget = Number(budgetValue.replace(/[^0-9.]/g, ''));
        if (!isNaN(budget)) {
          return `€${budget.toLocaleString()}`;
        }
        return budgetValue;
      }
      return String(budgetValue);
    } catch (error) {
      return 'Error displaying budget';
    }
  };
  return <div className="flex flex-col min-h-screen bg-gray-100" data-id="element-695">
      <NavigationBar data-id="element-696" />
      {/* Requirements Header */}
      <div className="w-[95%] mx-auto px-4 pt-4" data-id="element-697">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4" data-id="element-698">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center" data-id="element-699">
            <div className="flex-1" data-id="element-700">
              <h1 className="text-lg font-semibold text-gray-800" data-id="element-701">
                {sourcingEvent.requirements.title}
              </h1>
              <p className="text-gray-600 mt-1 text-sm" data-id="element-702">
                {sourcingEvent.requirements.description}
              </p>
              <div className="flex flex-wrap gap-4 mt-3" data-id="element-703">
                <div className="flex items-center text-sm text-gray-600" data-id="element-704">
                  <CalendarIcon size={16} className="mr-1 text-blue-600" data-id="element-705" />
                  <span className="font-medium mr-1" data-id="element-706">Target Date:</span>
                  {formatDate(sourcingEvent.requirements.deadline)}
                </div>
                <div className="flex items-center text-sm text-gray-600" data-id="element-707">
                  <DollarSignIcon size={16} className="mr-1 text-blue-600" data-id="element-708" />
                  <span className="font-medium mr-1" data-id="element-709">Budget:</span>
                  {formatBudget(sourcingEvent.requirements.budget)}
                </div>
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex items-center" data-id="element-710">
              <div className="flex items-center" data-id="element-711">
                <span className="text-sm font-medium text-gray-700 mr-2" data-id="element-712">
                  Auto-Negotiate
                </span>
                <button className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${autoNegotiate ? 'bg-blue-600' : 'bg-gray-200'}`} onClick={() => setAutoNegotiate(!autoNegotiate)} role="switch" aria-checked={autoNegotiate} data-id="element-713">
                  <span className="sr-only" data-id="element-714">Toggle auto-negotiation</span>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoNegotiate ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-715" />
                </button>
              </div>
              {autoNegotiate && <div className="ml-2 text-xs text-blue-600 flex items-center" data-id="element-716">
                  <ZapIcon size={14} className="mr-1" data-id="element-717" />
                  <span data-id="element-718">AI will automatically negotiate with suppliers</span>
                </div>}
            </div>
          </div>
        </div>
      </div>
      <div className="w-[95%] mx-auto px-4 pb-4 flex-1" data-id="element-719">
        <Outlet data-id="element-720" />
      </div>
    </div>;
};
export default SourcingEventLayout;